import { useContext } from 'react'
import { useMutation } from 'react-query'
import { apiPost, apiDelete } from 'api'
import { baseUrl } from 'api/helpers'
import axios from 'axios'
import AuthContext from 'context/auth-context'

const createDashboardAnalyticsColumn = async (token, params) => {
  const objectName = 'dashboard_analytics_column'
  const url = `dashboards/${params.dashboard_id}/dashboard_analytics_columns`
  const data = await apiPost({ url, token, objectName, params })
  return data
}

export function useCreateDashboardAnalyticsColumn({ params, options }) {
  const token = useContext(AuthContext)

  return useMutation(
    (mutationParams) => {
      // Support both single analytic_id and multiple analytic_ids
      const finalParams = mutationParams || params
      return createDashboardAnalyticsColumn(token, finalParams)
    },
    { ...options }
  )
}

const deleteDashboardAnalyticsColumn = async (token, id) => {
  // Support both single delete and batch delete with ids array
  if (Array.isArray(id)) {
    // For batch delete, use any valid ID in the URL and pass all IDs as params
    const fullUrl = `${baseUrl}/dashboard_analytics_columns/${id[0]}`
    
    const data = await axios.delete(fullUrl, {
      params: { ids: id },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    })
    return data.data
  } else {
    // Single delete - just use the ID in the URL
    const url = `dashboard_analytics_columns/${id}`
    const data = await apiDelete({ url, token })
    return data
  }
}

export function useDeleteDashboardAnalyticsColumn({ id, options }) {
  const token = useContext(AuthContext)

  return useMutation(
    (mutationIds) => {
      // Use mutationIds if provided, otherwise use id from config
      const idsToDelete = mutationIds !== undefined ? mutationIds : id
      return deleteDashboardAnalyticsColumn(token, idsToDelete)
    },
    { ...options }
  )
}

const reorderDashboardAnalyticsColumns = async (token, dashboardId, columnOrders) => {
  const fullUrl = `${baseUrl}/dashboards/${dashboardId}/dashboard_analytics_columns/reorder`
  const payload = { column_orders: columnOrders }

  const data = await axios.patch(fullUrl, payload, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  })
  return data.data
}

/**
 * Hook for reordering dashboard analytics columns
 * @param {string} dashboardId - Dashboard ID
 * @returns {Object} Mutation object with mutate function
 */
export function useReorderDashboardAnalyticsColumns(dashboardId) {
  const token = useContext(AuthContext)

  return useMutation((columnOrders) => {
    return reorderDashboardAnalyticsColumns(token, dashboardId, columnOrders)
  })
}